// ShareLinkModal — the "Share publicly" dialog for a file or a note
// (PUBLIC-SHARE-LINKS-1). Body-only: backdrop, ESC and dismiss-on-backdrop
// belong to @symbo.ls/windows' ModalLayer, exactly like AppCreateOrgModal.
//
// Opened from ANY surface with:
//   el.call('openShareModal', { targetType: 'file'|'note', targetId, name })
// All behaviour lives in workspace/functions/shareLinks.js — this file is
// the surface, and every action is one `el.call`.
//
// ⚠️ THE DIALOG IS THE ONLY PLACE THE URL EVER EXISTS
// The server keeps sha256(token), never the token. `_shareNewUrl` holds the
// freshly-minted link for the life of this dialog and nothing persists it,
// so the copy field is shown ONCE, right after minting. The list below it
// can show a link's state and revoke it, but can never show its URL again.
//
// What the dialog must make unmissable, because a public link is a security
// act and not a convenience: WHO can open it (anyone with the link), that
// the content is LIVE (Nika Q1 — it keeps serving future edits), and WHEN it
// stops (Nika Q2 — 30 days by default).

export const ShareLinkModal = {
  width: '100%',
  maxWidth: '90vw',
  flow: 'y',
  gap: 'A',
  // Cap the body height directly (not via an ancestor) so a short viewport
  // can never push ModalHeader — and its close X — off-screen. Same
  // reasoning as AppCreateOrgModal's root cap.
  maxHeight: '80vh',
  // No `overflow` here: Body is the one scroller (below), and a clip on this
  // root cut the brand ModalHeader's Close X — pulled into the window's
  // padding by a negative margin — by 13px (live on dev 2026-09-26).
  minHeight: '0',

  ModalHeader: {
    Hgroup: {
      H: { text: '{{ workspace.share.modal.title | polyglot }}' },
      P: {
        text: (el, s) => {
          const t = s.root?._shareTarget
          const name = t?.name
            ? `“${t.name}”`
            : el.call(
                'polyglot',
                t?.targetType === 'note'
                  ? 'workspace.share.modal.thisNote'
                  : 'workspace.share.modal.thisFile'
              )
          return el.call('polyglot', 'workspace.share.modal.subtitle').replace('{what}', name)
        }
      }
    },
    Icons: {
      Close: {
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('closeShareModal')
        }
      }
    }
  },

  Body: {
    flow: 'y',
    gap: 'Z1',
    flex: '1',
    minHeight: '0',
    overflowY: 'auto',

    // The two facts a person must have BEFORE they mint the link, not after.
    LiveNotice: {
      tag: 'p',
      fontSize: 'Y2',
      color: 'caption',
      margin: '0',
      text: '{{ workspace.share.modal.liveNotice | polyglot }}'
    },

    // The shared field kit (shared/components/AppField.js): AppField owns the
    // caption, AppSelect the control surface; this field keeps the behaviour
    // only. `label` is a FUNCTION prop — an attribute never resolves a
    // template.
    ExpiryField: {
      extends: 'AppField',
      label: (el) => el.call('polyglot', 'workspace.share.modal.expiryLabel'),

      AppSelect: {
        // The select's own accessible name stays its own key, as the flat
        // `ariaLabel` — the one form that replaces the kit's function, so ONE
        // effect owns aria-label.
        ariaLabel: (el) => el.call('polyglot', 'workspace.shareLink.expiryLabel'),
        value: (el, s) => s.root?._shareExpiryKey || '30',
        onChange: (e, el, s) => {
          s.root.update({ _shareExpiryKey: e.target.value })
        },
        // Static options — the four choices are a product decision, not data.
        // 30 days leads because the SAFE option must be the one a user gets
        // without choosing anything (Nika Q2).
        Days30: {
          tag: 'option',
          value: '30',
          text: '{{ workspace.share.modal.opt30 | polyglot }}'
        },
        Days7: { tag: 'option', value: '7', text: '{{ workspace.share.modal.opt7 | polyglot }}' },
        Days90: {
          tag: 'option',
          value: '90',
          text: '{{ workspace.share.modal.opt90 | polyglot }}'
        },
        Never: {
          tag: 'option',
          value: 'never',
          text: '{{ workspace.share.modal.optNever | polyglot }}'
        }
      }
    },

    // The kit's ink primary (was the brand Button with a hand-set scale), with
    // the house floor for a label that changes while it works.
    CreateBtn: {
      extends: 'AppCapsuleBtnPrimary',
      minWidth: 'E',
      alignSelf: 'flex-start',
      disabled: (el, s) => !!s.root?._shareBusy,
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root?._shareBusy ? 'workspace.shareLink.creating' : 'workspace.shareLink.create'
        ),
      onClick: (e, el) => {
        // markPageSaved: opt-out — the expiry pick is a mint option the dialog keeps for the next link
        e.preventDefault()
        el.call('createShareLinkNow')
      }
    },

    // Shown ONCE, immediately after minting. There is no way back to this
    // value — the server stores only the hash.
    // The kit field: its caption is the copy-once warning; the control sits
    // in a row beside the Copy button.
    NewLink: {
      extends: 'AppField',
      label: (el) => el.call('polyglot', 'workspace.share.modal.copyOnce'),
      display: null,
      hide: (el, s) => !s.root?._shareNewUrl,

      Row: {
        flow: 'x',
        gap: 'Z',
        align: 'center left',

        // The kit's control. Its parent is this Row, not the field, so it
        // names itself with its own key (the flat `ariaLabel`).
        AppInput: {
          type: 'text',
          readonly: true,
          ariaLabel: (el) => el.call('polyglot', 'workspace.shareLink.publicLinkLabel'),
          // Layout, not look: the link takes the row, the Copy button keeps
          // its own width.
          flex: '1',
          value: (el, s) => s.root?._shareNewUrl || '',
          onClick: (e) => e.target.select()
        },

        // The kit's tonal capsule at the field's own height (controlH).
        CopyBtn: {
          extends: 'AppCapsuleBtnToolbar',
          flexShrink: '0',
          text: '{{ workspace.share.modal.copy | polyglot }}',
          onClick: (e, el, s) => {
            e.preventDefault()
            el.call('copyShareLink', s.root?._shareNewUrl)
          }
        }
      }
    },

    // Everything already shared for this resource — the list that makes a
    // revoke an informed act rather than a guess.
    // A section head — the kit's mono micro-label.
    ExistingTitle: {
      extends: 'AppMicroLabel',
      tag: 'p',
      margin: 'Z 0 0',
      text: '{{ workspace.share.modal.existing | polyglot }}'
    },

    Empty: {
      tag: 'p',
      fontSize: 'Y2',
      color: 'caption',
      margin: '0',
      hide: (el, s) => !!(s.root?._shareLinks || []).length || !!s.root?._shareLoading,
      text: '{{ workspace.share.modal.empty | polyglot }}'
    },

    Links: {
      flow: 'y',
      gap: 'V',
      childExtends: 'ShareLinkRow',
      childrenAs: 'state',
      children: (el, s) => s.root?._shareLinks || []
    },

    Error: {
      hide: (el, s) => !s.root?._shareError,
      tag: 'p',
      role: 'alert',
      'aria-live': 'assertive',
      // The kit's AA-safe danger pair (designSystem/color.js §4.2) — this is
      // TEXT ink; brand red measures 2.82:1 on the light stage.
      color: 'dangerInk',
      fontSize: 'Y2',
      margin: 'Z 0 0',
      text: (el, s) => s.root?._shareError || ''
    }
  }
}

// One row of the existing-links list. Distinctive name (Rule 35) so it can
// never collide with a generic `Row`/`LinkRow` in another package's context.
export const ShareLinkRow = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'V 0',

  Status: {
    tag: 'span',
    flex: '1',
    fontSize: 'Y2',
    // A dead link reads as caption grey; a live one is the thing that still
    // exposes content, so it keeps the foreground colour.
    color: (el, s) => (s.live ? 'title' : 'caption'),
    text: (el, s) => el.call('shareLinkStatusLabel', s)
  },

  Uses: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    whiteSpace: 'nowrap',
    text: (el, s) => {
      const n = s.accessCount || 0
      if (!n) return el.call('polyglot', 'workspace.share.uses.never')
      return n === 1
        ? el.call('polyglot', 'workspace.share.uses.once')
        : el.call('polyglot', 'workspace.share.uses.many').replace('{count}', n)
    }
  },

  // Revoking a public link is destructive — the kit's danger capsule in its
  // compact step (was the brand Button with a hand-set scale and a
  // `display: null` to let `hide` beat its inline display).
  RevokeBtn: {
    extends: 'AppCapsuleBtnDanger',
    compact: true,
    flexShrink: '0',
    // A revoked or expired link has nothing left to revoke.
    hide: (el, s) => !s.live,
    disabled: (el, s) => !!s.root?._shareBusy,
    text: '{{ workspace.share.modal.revoke | polyglot }}',
    // Asks first (functions/shareLinks.js confirmRevokeShareLink).
    onClick: (e, el, s) => {
      e.preventDefault()
      el.call('confirmRevokeShareLink', s.id)
    }
  }
}
